import { Turnstile } from '@marsidev/react-turnstile';
import { useMask } from '@react-input/mask';
import {
  generateLeadURL,
  getCookieGoogleId,
  getCookieMsclkid
} from 'app/actions/affiliate.actions';
import { siteVerification } from 'app/actions/cloudflare.actions';
import { getTurnstileKey } from 'app/actions/turnstile.actions';
import { Label, TextInput } from 'flowbite-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { memo, useEffect, useRef, useState } from 'react';

const TurnstileVerification = memo(({ 
  turnstileKey, 
  setTurnstileToken, 
  setError 
}: { 
  turnstileKey: string | null, 
  setTurnstileToken: (token: string) => void, 
  setError: (error: string | null) => void 
}) => {
  if (!turnstileKey) return null;
  
  return (
    <div role="img" aria-label="Security verification">
      <Turnstile
        siteKey={turnstileKey}
        onError={(e) => {
          setTurnstileToken('');
          setError('Security verification failed. Please try again.');
        }}
        onExpire={(e) => {
          setTurnstileToken('');
          setError('Security verification expired. Please verify again.');
        }}
        onSuccess={(e) => {
          setTurnstileToken(e);
          setError(null);
        }}
      />
    </div>
  );
});

TurnstileVerification.displayName = 'TurnstileVerification';

interface RequestQuoteWizardFormProps {
  form: any;
  sourceKey: string;
  setForm: (value: any) => void;
  variant: string;
}

export function RequestQuoteWizardForm(props: RequestQuoteWizardFormProps) {
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  
  const errorRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setTurnstileKey(getTurnstileKey());
  }, []);

  // Focus error message when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { form, variant, sourceKey } = props;

  const inputRef = useMask({
    mask: '(___) ___-____',
    replacement: { _: /\d/ }
  });

  async function createQuote(formData: FormData) {
    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    if (!turnstileToken) {
      setError('Please verify you are not a robot');
      setIsSubmitting(false);
      return;
    }

    let gclid = await getCookieGoogleId();
    let msclkid = await getCookieMsclkid();

    try {
      const status = await siteVerification(turnstileToken);

      if (status === 200) {
        const rawFormData = {
          firstName: formData.get('firstName'),
          lastName: formData.get('lastName'),
          email: formData.get('email'),
          phone: formData.get('phone')
        };

        const payload = {
          contact: {
            isResidential: true,
            firstName: rawFormData.firstName,
            lastName: rawFormData.lastName,
            email: rawFormData.email,
            phone: {
              number: rawFormData.phone
            }
          },
          compliance: {
            tcpaPermissionGranted: true,
            tcpaPermissionLink: ''
          },
          additionalFields: {
            FIRSTNAME: rawFormData.firstName,
            LASTNAME: rawFormData.lastName,
            EMAIL: rawFormData.email,
            PHONE: rawFormData.phone,
            submissionURL: window.location.href,
            ...form
          } as any
        };

        const subid = searchParams?.get('subid');
        if (subid) {
          payload.additionalFields['subid'] = subid;
        }

        const leadURL = await generateLeadURL(sourceKey);
        if (gclid !== undefined) payload.additionalFields['googleclickid'] = gclid;
        if (msclkid !== undefined) payload.additionalFields['msclkid'] = msclkid;

        if (leadURL) {
          const response = await fetch(leadURL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });
          if (response.status === 200) {
            router.push('/thank-you');
          } else {
            setError('Failed to submit form. Please try again.');
          }
        } else {
          setError('Unable to process request. Please try again.');
        }
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (e) {
      console.log(e);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div>
      {variant === 'default' && (
        <div className="flex flex-col items-center">
          <div className="w-full text-center">
            <p>Speak with a Security Advisor:</p>
            <p>or</p>
            <p>Let us reach out and customize a quote for you</p>
          </div>

          {/* Error announcement region */}
          {error && (
            <div
              ref={errorRef}
              role="alert"
              aria-live="polite"
              className="mt-4 w-full max-w-md rounded border border-red-400 bg-red-50 p-3 text-red-700"
              tabIndex={-1}
            >
              {error}
            </div>
          )}

          <form className="mt-4 flex w-full flex-col gap-4 px-20" action={createQuote}>
            <fieldset className="border-none p-0 flex flex-col gap-[10px]">
              <legend className="sr-only">Personal Information</legend>
              
              <div className="flex w-full gap-2">
                <div className="flex-1">
                  <Label htmlFor="firstName" value="First Name *" />
                  <TextInput 
                    id="firstName"
                    name="firstName" 
                    type="text" 
                    required 
                    shadow
                    aria-invalid={fieldErrors.firstName ? 'true' : 'false'}
                    aria-describedby={fieldErrors.firstName ? 'firstName-error' : undefined}
                  />
                  {fieldErrors.firstName && (
                    <p id="firstName-error" className="mt-1 text-sm text-red-600" role="alert">
                      {fieldErrors.firstName}
                    </p>
                  )}
                </div>
                <div className="flex-1">
                  <Label htmlFor="lastName" value="Last Name *" />
                  <TextInput 
                    id="lastName"
                    name="lastName" 
                    type="text" 
                    required 
                    shadow
                    aria-invalid={fieldErrors.lastName ? 'true' : 'false'}
                    aria-describedby={fieldErrors.lastName ? 'lastName-error' : undefined}
                  />
                  {fieldErrors.lastName && (
                    <p id="lastName-error" className="mt-1 text-sm text-red-600" role="alert">
                      {fieldErrors.lastName}
                    </p>
                  )}
                </div>
              </div>
              
              <div>
                <Label htmlFor="phone" value="Phone Number *" />
                <TextInput
                  id="phone"
                  pattern="\([0-9]{3}\) [0-9]{3}-[0-9]{4}"
                  ref={inputRef}
                  name="phone"
                  type="tel"
                  required
                  shadow
                  aria-invalid={fieldErrors.phone ? 'true' : 'false'}
                  aria-describedby="phone-help phone-error"
                />
                <p id="phone-help" className="mt-1 text-sm text-gray-600">
                  Format: (123) 456-7890
                </p>
                {fieldErrors.phone && (
                  <p id="phone-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.phone}
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="email" value="Email Address *" />
                <TextInput 
                  id="email"
                  name="email" 
                  type="email" 
                  required 
                  shadow
                  aria-invalid={fieldErrors.email ? 'true' : 'false'}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                />
                {fieldErrors.email && (
                  <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.email}
                  </p>
                )}
              </div>
            </fieldset>

            <div className="mt-2 flex w-full flex-col items-center justify-center gap-4">
              <TurnstileVerification 
                turnstileKey={turnstileKey} 
                setTurnstileToken={setTurnstileToken} 
                setError={setError} 
              />

              <button
                ref={submitButtonRef}
                type="submit"
                disabled={isSubmitting}
                className="flex w-full max-w-48 items-center justify-center rounded-md border-2 bg-fp-green p-2 font-bold text-white hover:border-2 hover:border-fp-green hover:bg-white hover:text-fp-green disabled:cursor-not-allowed disabled:opacity-50"
                aria-describedby="submit-help"
              >
                {isSubmitting ? (
                  <>
                    <span className="sr-only">Submitting...</span>
                    <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25"/>
                      <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
              <p id="submit-help" className="sr-only">
                By clicking Submit, you agree to receive marketing communications.
              </p>
            </div>
          </form>
          
          <div className="w-full px-10 pt-4 text-justify">
            <p className="text-[12px]">
              By submitting your phone number, you authorize Frontpoint to send you marketing
              communications by phone. Calls may be made using an automatic telephone dialing system
              and may play a recorded message when a connection is complete or transmit a
              pre-recorded voicemail.
            </p>
          </div>
        </div>
      )}

      {variant === 'lifecare' && (
        <div className="items-left flex flex-col">
          <div className="text-left">
            <h2 className="text-[22px] font-bold text-fp-green">Get Your Free Quote</h2>
          </div>

          {/* Error announcement region */}
          {error && (
            <div
              ref={errorRef}
              role="alert"
              aria-live="polite"
              className="mt-4 rounded border border-red-400 bg-red-50 p-3 text-red-700"
              tabIndex={-1}
            >
              {error}
            </div>
          )}

          <form className="mt-4 flex w-full flex-col gap-4" action={createQuote}>
            <fieldset className="border-none p-0 flex flex-col gap-[10px]">
              <legend className="sr-only">Contact Information</legend>
              
              <div>
                <Label htmlFor="firstName-lc" value="First Name *" />
                <TextInput
                  id="firstName-lc"
                  className="[&_*]:rounded-3xl [&_*]:bg-fp-sand"
                  name="firstName"
                  type="text"
                  required
                  shadow
                  aria-invalid={fieldErrors.firstName ? 'true' : 'false'}
                  aria-describedby={fieldErrors.firstName ? 'firstName-lc-error' : undefined}
                />
                {fieldErrors.firstName && (
                  <p id="firstName-lc-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.firstName}
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="lastName-lc" value="Last Name *" />
                <TextInput
                  id="lastName-lc"
                  className="[&_*]:rounded-3xl [&_*]:bg-fp-sand"
                  name="lastName"
                  type="text"
                  required
                  shadow
                  aria-invalid={fieldErrors.lastName ? 'true' : 'false'}
                  aria-describedby={fieldErrors.lastName ? 'lastName-lc-error' : undefined}
                />
                {fieldErrors.lastName && (
                  <p id="lastName-lc-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.lastName}
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="phone-lc" value="Phone Number *" />
                <TextInput
                  id="phone-lc"
                  className="[&_*]:rounded-3xl [&_*]:bg-fp-sand"
                  pattern="\([0-9]{3}\) [0-9]{3}-[0-9]{4}"
                  ref={inputRef}
                  name="phone"
                  type="tel"
                  required
                  shadow
                  aria-invalid={fieldErrors.phone ? 'true' : 'false'}
                  aria-describedby="phone-lc-help phone-lc-error"
                />
                <p id="phone-lc-help" className="mt-1 text-sm text-gray-600">
                  Format: (123) 456-7890
                </p>
                {fieldErrors.phone && (
                  <p id="phone-lc-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.phone}
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="email-lc" value="Email Address *" />
                <TextInput
                  id="email-lc"
                  className="[&_*]:rounded-3xl [&_*]:bg-fp-sand"
                  name="email"
                  type="email"
                  required
                  shadow
                  aria-invalid={fieldErrors.email ? 'true' : 'false'}
                  aria-describedby={fieldErrors.email ? 'email-lc-error' : undefined}
                />
                {fieldErrors.email && (
                  <p id="email-lc-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.email}
                  </p>
                )}
              </div>
            </fieldset>

            <div className="mt-2 flex w-full justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex w-full items-center justify-center rounded-3xl border-2 bg-fp-green p-3 font-bold text-white hover:border-2 hover:border-fp-green hover:bg-white hover:text-fp-green disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <span className="sr-only">Submitting...</span>
                    <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25"/>
                      <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Get a Free Quote Now'
                )}
              </button>
            </div>
          </form>
          
          <div className="w-full pt-2 text-justify">
            <div className="flex justify-center">
              <TurnstileVerification 
                turnstileKey={turnstileKey} 
                setTurnstileToken={setTurnstileToken} 
                setError={setError} 
              />
            </div>
            <p className="text-[9px]">
              By submitting your phone number, you authorize Frontpoint to send you marketing
              communications by phone. Calls may be made using an automatic telephone dialing
              system and may play a recorded message when a connection is complete or transmit a
              pre-recorded voicemail.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
